<template>
  <div class="server-logs-tab">
    <div v-if="retentionDays !== null" class="server-logs-tab__tier-notice">
      <strong>{{ retentionDays }} day{{ retentionDays === 1 ? '' : 's' }} of log history</strong> on this tier's retention window.
    </div>

    <div class="server-logs-tab__layout">
      <div class="server-logs-tab__rail">
        <div class="server-logs-tab__rail-title server-logs-tab__rail-title--row">
          <span>Domains</span>
          <button
            v-if="selectedDomains.length"
            type="button"
            class="server-logs-tab__rail-clear"
            @click="clearDomains"
          >Clear</button>
        </div>
        <div
          class="server-logs-tab__source-row"
          :class="{ 'server-logs-tab__source-row--active': selectedDomains.length === 0 }"
          @click="clearDomains"
        >All domains</div>
        <div
          v-for="d in domains"
          :key="d.domain"
          class="server-logs-tab__source-row"
          :class="{ 'server-logs-tab__source-row--active': selectedDomains.includes(d.domain) }"
          @click="toggleDomain(d.domain)"
        >
          <span
            class="server-logs-tab__check"
            :class="{ 'server-logs-tab__check--on': selectedDomains.includes(d.domain) }"
          ></span>
          <span class="server-logs-tab__source-name">{{ d.domain }}</span>
        </div>
        <div v-if="!domains.length" class="server-logs-tab__empty-rail">No domain-tagged logs yet.</div>

        <div class="server-logs-tab__rail-title server-logs-tab__rail-title--spaced server-logs-tab__rail-title--row">
          <span>Files</span>
          <button
            type="button"
            class="server-logs-tab__refresh-btn"
            :disabled="refreshingSources"
            :title="refreshSourcesError || 'Ask the agent for its current log-source catalogue'"
            @click="refreshSources"
          >{{ refreshingSources ? 'Refreshing…' : 'Refresh sources' }}</button>
        </div>
        <div v-if="refreshSourcesError" class="server-logs-tab__refresh-error">{{ refreshSourcesError }}</div>
        <div
          class="server-logs-tab__source-row"
          :class="{ 'server-logs-tab__source-row--active': selectedPath === null }"
          @click="selectedPath = null; load()"
        >All sources</div>
        <div
          v-for="source in sources"
          :key="source.path"
          class="server-logs-tab__source-row"
          :class="{ 'server-logs-tab__source-row--active': selectedPath === source.path }"
          @click="selectedPath = source.path; load()"
        >
          <span class="server-logs-tab__source-name">{{ source.path }}</span>
          <button
            type="button"
            class="server-logs-tab__sub-toggle"
            :class="{ 'server-logs-tab__sub-toggle--on': source.subscribed }"
            :title="source.subscribed ? 'Subscribed — click to stop tailing' : 'Not subscribed — click to start tailing'"
            @click.stop="toggleSubscription(source)"
          >{{ source.subscribed ? 'On' : 'Off' }}</button>
        </div>
        <div v-if="!sources.length" class="server-logs-tab__empty-rail">No log sources ingested yet.</div>
      </div>

      <div class="server-logs-tab__log-card">
        <div class="server-logs-tab__toolbar">
          <input
            v-model="searchQuery"
            type="text"
            class="server-logs-tab__search"
            placeholder="Search recent window…"
            @keyup.enter="load()"
          />
          <div class="server-logs-tab__chip-row">
            <span
              class="server-logs-tab__sev-chip"
              :class="{ 'server-logs-tab__sev-chip--active-all': severities.length === 0 && !errorsAndWorse }"
              @click="clearSeverity()"
            >All</span>
            <span
              class="server-logs-tab__sev-chip server-logs-tab__sev-chip--worse"
              :class="{ 'server-logs-tab__sev-chip--active-worse': errorsAndWorse }"
              title="Warning, error and critical lines only — the most common triage view"
              @click="toggleErrorsAndWorse()"
            >Errors &amp; worse</span>
            <span
              v-for="sev in ['info', 'warning', 'error', 'critical']"
              :key="sev"
              class="server-logs-tab__sev-chip"
              :class="{ [`server-logs-tab__sev-chip--active-${sev}`]: severities.includes(sev) }"
              @click="toggleSeverity(sev)"
            >{{ sev }}</span>
          </div>
          <div class="server-logs-tab__chip-row">
            <span
              v-for="group in CATEGORY_GROUPS"
              :key="group.value"
              class="server-logs-tab__type-chip"
              :class="{ 'server-logs-tab__type-chip--active': categoryGroups.includes(group.value) }"
              @click="toggleCategoryGroup(group.value)"
            >{{ group.label }}</span>
          </div>
          <select v-model="timeRange" class="server-logs-tab__range" @change="load()">
            <option value="">All time</option>
            <option value="3600">Last hour</option>
            <option value="86400">Last 24h</option>
            <option value="604800">Last 7d</option>
          </select>
          <label class="server-logs-tab__follow">
            <span v-if="following" class="server-logs-tab__pulse"></span>
            Follow
            <input type="checkbox" v-model="following" @change="onFollowToggle" />
          </label>
          <button type="button" class="server-logs-tab__icon-btn" title="Search" @click="load()">Search</button>
        </div>

        <div v-if="activeFilterChips.length" class="server-logs-tab__active-filters">
          <span
            v-for="chip in activeFilterChips"
            :key="chip.key"
            class="server-logs-tab__active-chip"
          >
            {{ chip.label }}
            <button type="button" class="server-logs-tab__active-chip-x" title="Remove filter" @click="chip.clear()">&times;</button>
          </span>
          <button type="button" class="server-logs-tab__clear-all" @click="clearAllFilters">Clear all</button>
        </div>

        <div class="server-logs-tab__tail">
          <template v-for="entry in visibleEntries" :key="entry.id">
            <div v-if="entry.is_gap_marker" class="server-logs-tab__gap">
              <span v-if="!suppressSourceBadge" class="server-logs-tab__src" :title="entry.log_path">{{ entry._source }}</span>
              &#8942; {{ entry._expanded ? entry.line : entry._displayLine }}<button
                v-if="entry._truncated"
                type="button"
                class="server-logs-tab__expand-btn"
                @click="toggleExpand(entry)"
              >{{ entry._expanded ? '(show less)' : '(show full line)' }}</button>
            </div>
            <div v-else class="server-logs-tab__line">
              <span class="server-logs-tab__time">{{ entry._time }}</span>
              <span class="server-logs-tab__lvl" :class="`lvl-${entry.severity}`">{{ entry.severity.toUpperCase() }}</span>
              <span v-if="!suppressSourceBadge" class="server-logs-tab__src" :title="entry.log_path">{{ entry._source }}</span>
              {{ entry._expanded ? entry.line : entry._displayLine }}<button
                v-if="entry._truncated"
                type="button"
                class="server-logs-tab__expand-btn"
                @click="toggleExpand(entry)"
              >{{ entry._expanded ? '(show less)' : '(show full line)' }}</button>
            </div>
          </template>
          <div v-if="entries.length > visibleEntries.length" class="server-logs-tab__show-more">
            <button type="button" class="server-logs-tab__show-more-btn" @click="showMore">Show {{ Math.min(RENDER_WINDOW, entries.length - visibleEntries.length) }} more (of {{ entries.length }} loaded)</button>
          </div>
          <div v-if="!entries.length" class="server-logs-tab__no-entries">
            <template v-if="!sources.length">No log sources discovered yet on this server — click "Refresh sources" above to ask the agent for its current catalogue.</template>
            <template v-else-if="selectedSource && !selectedSource.subscribed">
              <div>"{{ shortSourceLabel(selectedSource.path) || selectedSource.path }}" is discovered but not subscribed, so it is not being tailed.</div>
              <button type="button" class="server-logs-tab__subscribe-cta" @click="subscribeSelectedSource">Subscribe to this log</button>
            </template>
            <template v-else-if="!hasSubscribedSource">No sources are subscribed yet. Turn on a source in the Files list (or Refresh sources to discover more) to start collecting logs here.</template>
            <template v-else>No log entries match the current filters.</template>
          </div>
        </div>

        <div class="server-logs-tab__footer">
          <span>Showing {{ visibleEntries.length }} of {{ entries.length }} entr{{ entries.length === 1 ? 'y' : 'ies' }}{{ hasMore ? ' (more available)' : '' }}</span>
          <span v-if="following">Following — polling every {{ pollIntervalSeconds }}s</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';

// `logs-view.html` — full working Logs tab. Follow mode is a plain poll
// loop (`from: lastReceivedAt + 1` on each tick), NOT a websocket/SSE
// stream — per the brief, "poll the logs endpoint with sinceTs while
// active, no websocket client-side today". `is_gap_marker` rows (ingest
// discontinuity markers — dropped bytes / log rotation) render as a
// distinct dashed line rather than a normal severity-coloured entry.
//
// Source-type quick filters (System/Web error/Web access/DB/Security) mirror
// the backend's `LogCategoryGroup` values 1:1 — keep this list and
// `LogSearchService::CATEGORY_GROUP_TOKENS` in sync if a group is ever added.
const CATEGORY_GROUPS = [
  { value: 'system', label: 'System' },
  { value: 'web-error', label: 'Web error' },
  { value: 'web-access', label: 'Web access' },
  { value: 'db', label: 'DB' },
  { value: 'security', label: 'Security' },
];

const props = defineProps({
  apiBase: { type: String, required: true },
  serverId: { type: [Number, String], required: true },
});

const api = useApi();

const sources = ref([]);
const domains = ref([]);
const entries = ref([]);
const hasMore = ref(false);

// Perf: never mount/lay-out the whole loaded batch at once. `entries` is
// already newest-first (see the poll comment below), so slicing the first
// `renderLimit` is a free "latest N" view — new lines that arrive via
// follow-mode polling land at the front and simply push the window forward
// with no extra bookkeeping. "Show more" just widens the window over data
// already in memory (no refetch). This is what turned a 200-row unwindowed
// mount + per-interaction full-relayout into the dominant cost of opening
// the tab; RENDER_WINDOW is picked well under the ~200-entry batch that was
// observed freezing the renderer.
const RENDER_WINDOW = 100;
const renderLimit = ref(RENDER_WINDOW);
const visibleEntries = computed(() => entries.value.slice(0, renderLimit.value));
function showMore() {
  renderLimit.value += RENDER_WINDOW;
}
const retentionDays = ref(null);
const selectedPath = ref(null);
const selectedDomains = ref([]);
const searchQuery = ref('');
const severities = ref([]);
const errorsAndWorse = ref(false);
const categoryGroups = ref([]);
const timeRange = ref('');
const following = ref(false);
const pollIntervalSeconds = 5;
let pollHandle = null;
let lastReceivedAt = null;

const refreshingSources = ref(false);
const refreshSourcesError = ref(null);
let refreshPollHandle = null;

// Distinguishes the "nothing to show" states asked for: a source
// subscribed-but-quiet window ("no entries match") vs. no error sources
// subscribed at all yet (guide the user to the Files list / Refresh sources)
// vs. the one specific file the user picked being discovered-but-unsubscribed
// (a per-source state — hasSubscribedSource alone can't distinguish this,
// since some OTHER source on the server may well be subscribed already).
const hasSubscribedSource = computed(() => sources.value.some((s) => s.subscribed));

// Only meaningful when a specific file is selected — the matching catalogue
// entry, or undefined if the selected path isn't (or is no longer) in the
// discovered list.
const selectedSource = computed(() => (selectedPath.value ? sources.value.find((s) => s.path === selectedPath.value) : null));

// A domain or file filter that already pins the view to exactly one source
// makes a per-line source badge redundant noise — shown only once via the
// filter chips instead. With "all sources" (or 2+ domains) visible, every
// line needs its own badge so mixed-source output stays legible.
const suppressSourceBadge = computed(() => selectedDomains.value.length === 1 || !!selectedPath.value);

const activeFilterChips = computed(() => {
  const chips = [];
  if (selectedPath.value) {
    chips.push({ key: 'path', label: `File: ${shortSourceLabel(selectedPath.value) || selectedPath.value}`, clear: () => { selectedPath.value = null; load(); } });
  }
  for (const d of selectedDomains.value) {
    chips.push({ key: `domain:${d}`, label: d, clear: () => toggleDomain(d) });
  }
  for (const g of categoryGroups.value) {
    chips.push({ key: `cat:${g}`, label: categoryGroupLabel(g), clear: () => toggleCategoryGroup(g) });
  }
  if (errorsAndWorse.value) {
    chips.push({ key: 'errs', label: 'Errors & worse', clear: () => toggleErrorsAndWorse() });
  }
  for (const s of severities.value) {
    chips.push({ key: `sev:${s}`, label: s, clear: () => toggleSeverity(s) });
  }
  if (searchQuery.value) {
    chips.push({ key: 'q', label: `"${searchQuery.value}"`, clear: () => { searchQuery.value = ''; load(); } });
  }
  if (timeRange.value) {
    chips.push({ key: 'range', label: rangeLabel(timeRange.value), clear: () => { timeRange.value = ''; load(); } });
  }
  return chips;
});

function categoryGroupLabel(value) {
  return CATEGORY_GROUPS.find((g) => g.value === value)?.label || value;
}

function rangeLabel(value) {
  return { '3600': 'Last hour', '86400': 'Last 24h', '604800': 'Last 7d' }[value] || value;
}

function toggleSeverity(sev) {
  errorsAndWorse.value = false;
  const idx = severities.value.indexOf(sev);
  if (idx === -1) {
    severities.value.push(sev);
  } else {
    severities.value.splice(idx, 1);
  }
  load();
}

function clearSeverity() {
  severities.value = [];
  errorsAndWorse.value = false;
  load();
}

function toggleErrorsAndWorse() {
  errorsAndWorse.value = !errorsAndWorse.value;
  if (errorsAndWorse.value) {
    severities.value = [];
  }
  load();
}

function toggleCategoryGroup(group) {
  const idx = categoryGroups.value.indexOf(group);
  if (idx === -1) {
    categoryGroups.value.push(group);
  } else {
    categoryGroups.value.splice(idx, 1);
  }
  load();
}

function toggleDomain(domain) {
  const idx = selectedDomains.value.indexOf(domain);
  if (idx === -1) {
    selectedDomains.value.push(domain);
  } else {
    selectedDomains.value.splice(idx, 1);
  }
  load();
}

function clearDomains() {
  selectedDomains.value = [];
  load();
}

function clearAllFilters() {
  selectedPath.value = null;
  selectedDomains.value = [];
  categoryGroups.value = [];
  errorsAndWorse.value = false;
  severities.value = [];
  searchQuery.value = '';
  timeRange.value = '';
  load();
}

async function toggleSubscription(source) {
  const next = !source.subscribed;
  // Optimistic — the backend persists the intent even when the hub is
  // unreachable, so reflect it immediately and reconcile from the reload.
  source.subscribed = next;
  try {
    await api.post(`${props.apiBase}/${props.serverId}/logs/subscribe`, { path: source.path, subscribe: next });
  } catch {
    source.subscribed = !next; // roll back on a hard failure
  }
}

// The empty-state "Subscribe to this log" CTA for the currently-selected
// file — a deliberate, explicit opt-in click (never automatic on select),
// since high-volume access logs shouldn't start tailing just because someone
// looked at them. Reloads afterwards so the (still likely empty, until the
// agent actually starts tailing) view reflects the new subscription state.
async function subscribeSelectedSource() {
  if (!selectedSource.value) return;
  await toggleSubscription(selectedSource.value);
  await load();
}

function formatTime(epochSeconds) {
  if (!epochSeconds) return '';
  const d = new Date(epochSeconds * 1000);
  return d.toLocaleTimeString([], { hour12: false });
}

// The per-line source indicator: the tagged `domain` when present (e.g.
// `api.canalnav.com`), else a compact `category:basename` form of the raw
// path (e.g. `system:syslog`, `apache:error` — `.log` stripped) so mixed-
// source output stays legible without a full path per line.
function shortSourceLabel(path) {
  if (!path) return null;
  const base = path.split('/').filter(Boolean).pop() || path;
  return base.replace(/\.log$/i, '');
}

function sourceLabel(entry) {
  if (entry.domain) return entry.domain;
  const base = shortSourceLabel(entry.log_path) || 'unknown';
  return entry.category ? `${entry.category}:${base}` : base;
}

// The backend stores `line` as an uncapped `text` column (no truncation at
// ingest) — a raw access/error log line can legitimately contain one huge
// unbroken run of characters (a long querystring, a JWT, a base64 blob, an
// unwrapped stack-trace path). Combined with `word-break: break-word` on
// `.server-logs-tab__line/__gap` (needed so normal long lines wrap instead
// of overflowing), the browser's line-breaking pass over one enormous
// unbroken token is measurably expensive and re-runs on every layout —
// i.e. on every interaction, not just first paint. Cap what actually goes
// through that CSS by default; the full line is still one click away.
const LINE_TRUNCATE_AT = 2000;

// Precomputed once per entry when a batch arrives, rather than being called
// inline in the template (`formatTime(entry.line_ts)`, `sourceLabel(entry)`)
// where they'd re-run for every visible row on every re-render — typing in
// the search box, toggling a chip, or the follow-mode poll tick all touch
// reactive state the template reads, which re-evaluates the whole v-for.
// None of these three fields depend on anything but the entry itself, so
// computing them once and reading `entry._time`/`_source`/`_displayLine`
// from the template is a pure win with no staleness risk.
function decorateEntry(entry) {
  entry._time = formatTime(entry.line_ts);
  entry._source = sourceLabel(entry);
  const line = entry.line || '';
  entry._truncated = line.length > LINE_TRUNCATE_AT;
  entry._displayLine = entry._truncated ? `${line.slice(0, LINE_TRUNCATE_AT)}…` : line;
  entry._expanded = false;
  return entry;
}

function toggleExpand(entry) {
  entry._expanded = !entry._expanded;
}

function buildParams(extra = {}) {
  const params = { limit: 200, ...extra };
  if (selectedPath.value) params.path = selectedPath.value;
  if (selectedDomains.value.length) params.domain = selectedDomains.value.join(',');
  if (searchQuery.value) params.q = searchQuery.value;
  if (errorsAndWorse.value) {
    params.severity_min = 'warning';
  } else if (severities.value.length) {
    params.severity = severities.value.join(',');
  }
  if (categoryGroups.value.length) params.category_group = categoryGroups.value.join(',');
  // `timeRange` is a look-back window in seconds; translate to an absolute
  // `from` at request time (unless a live poll already passed its own `from`).
  if (timeRange.value && !('from' in extra)) {
    params.from = Math.floor(Date.now() / 1000) - Number(timeRange.value);
  }
  return params;
}

async function load() {
  try {
    const data = await api.get(`${props.apiBase}/${props.serverId}/logs`, { params: buildParams() });
    sources.value = data.paths || [];
    domains.value = data.domains || [];
    entries.value = (data.entries || []).map(decorateEntry);
    hasMore.value = !!data.has_more;
    retentionDays.value = data.retention_days ?? null;
    renderLimit.value = RENDER_WINDOW; // fresh query — start windowed again
    lastReceivedAt = entries.value.length ? Math.max(...entries.value.map((e) => e.received_at)) : Math.floor(Date.now() / 1000);
  } catch {
    // Empty state renders either way.
  }
}

async function pollForNew() {
  try {
    const data = await api.get(`${props.apiBase}/${props.serverId}/logs`, {
      params: buildParams({ from: (lastReceivedAt || 0) + 1 }),
    });
    const fresh = data.entries || [];
    if (fresh.length) {
      // Newest-first from the API — prepend to keep the same ordering.
      entries.value = [...fresh.map(decorateEntry), ...entries.value].slice(0, 500);
      lastReceivedAt = Math.max(lastReceivedAt || 0, ...fresh.map((e) => e.received_at));
    }
  } catch {
    // Transient failure — next tick tries again.
  }
}

function onFollowToggle() {
  if (following.value) {
    pollHandle = setInterval(pollForNew, pollIntervalSeconds * 1000);
  } else if (pollHandle) {
    clearInterval(pollHandle);
    pollHandle = null;
  }
}

// "Refresh sources" — the guaranteed on-demand trigger for a `logs.list`
// catalogue refresh (complements the backend's auto-trigger on tab load).
// Issues the command, then polls the same `load()` used everywhere else in
// this tab until the source count grows or ~30s elapses (mirrors the
// introspection tabs' `useIntrospection` refresh+poll UX).
async function refreshSources() {
  if (refreshingSources.value) return;
  refreshingSources.value = true;
  refreshSourcesError.value = null;
  const beforeCount = sources.value.length;

  try {
    await api.post(`${props.apiBase}/${props.serverId}/logs/refresh-sources`, {});
  } catch (err) {
    refreshSourcesError.value = err?.response?.data?.error || 'Could not issue the refresh.';
    refreshingSources.value = false;
    return;
  }

  let attempts = 0;
  const poll = async () => {
    attempts += 1;
    await load();
    if (sources.value.length > beforeCount) {
      refreshingSources.value = false;
      return;
    }
    if (attempts >= 10) {
      refreshingSources.value = false;
      refreshSourcesError.value = 'No new sources yet — the agent may be offline or still discovering.';
      return;
    }
    refreshPollHandle = setTimeout(poll, 3000);
  };
  refreshPollHandle = setTimeout(poll, 3000);
}

onBeforeUnmount(() => {
  if (pollHandle) {
    clearInterval(pollHandle);
  }
  if (refreshPollHandle) {
    clearTimeout(refreshPollHandle);
  }
});

load();
</script>

<style lang="scss">
.server-logs-tab {
  &__tier-notice {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #e4f1fa;
    border: 1px solid #b7d9ec;
    border-radius: var(--theme-radius, 6px);
    padding: 10px 14px;
    font-size: 12px;
    color: #1f5c80;
    margin-bottom: 14px;

    strong {
      color: #164760;
    }
  }

  &__layout {
    display: grid;
    grid-template-columns: 236px 1fr;
    gap: 16px;
    align-items: start;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  }

  &__rail {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    padding: 14px 0 16px;
  }

  &__rail-title {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .06em;
    color: var(--theme-text-tertiary);
    padding: 0 16px 8px;

    &--spaced {
      margin-top: 14px;
      padding-top: 12px;
      border-top: 1px solid var(--theme-border-soft);
    }

    &--row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }
  }

  &__rail-clear {
    font-size: 9.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .03em;
    color: var(--theme-accent-dark, #1f5c80);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
  }

  &__refresh-btn {
    font-size: 9.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .03em;
    padding: 3px 8px;
    border-radius: var(--theme-radius-sm, 3px);
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);
    color: var(--theme-text-secondary);
    cursor: pointer;

    &:disabled {
      opacity: .6;
      cursor: default;
    }
  }

  &__refresh-error {
    padding: 4px 16px 8px;
    font-size: 10.5px;
    color: #a53232;
  }

  &__source-row {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 7px 16px;
    font-size: 12.5px;
    color: var(--theme-text-secondary);
    cursor: pointer;

    &:hover {
      background: var(--theme-bg-content);
    }

    &--active {
      background: var(--theme-accent-light);
      color: var(--theme-accent-dark);
      font-weight: 600;
    }
  }

  &__check {
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    border-radius: 3px;
    border: 1px solid var(--theme-border-strong);
    background: var(--theme-bg-card);

    &--on {
      background: var(--theme-accent-dark, #1f5c80);
      border-color: var(--theme-accent-dark, #1f5c80);
    }
  }

  &__source-name {
    flex: 1;
    min-width: 0;
    font-family: var(--theme-font-mono);
    font-size: 11.5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__sub-toggle {
    flex-shrink: 0;
    font-size: 9.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .04em;
    padding: 2px 7px;
    border-radius: 10px;
    border: 1px solid var(--theme-border);
    background: var(--theme-bg-card);
    color: var(--theme-text-tertiary);
    cursor: pointer;

    &--on {
      background: var(--theme-status-success, #2e9e6b);
      border-color: var(--theme-status-success, #2e9e6b);
      color: #fff;
    }
  }

  &__range {
    font-size: 11.5px;
    padding: 6px 8px;
    border-radius: var(--theme-radius, 6px);
    border: 1px solid var(--theme-border);
    background: var(--theme-bg-card);
    color: var(--theme-text-secondary);
    cursor: pointer;
  }

  &__empty-rail {
    padding: 10px 16px;
    font-size: 11.5px;
    color: var(--theme-text-tertiary);
  }

  &__log-card {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    overflow: hidden;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--theme-border-soft);
    flex-wrap: wrap;
  }

  &__search {
    flex: 1;
    min-width: 200px;
    background: var(--theme-bg-content);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius, 6px);
    padding: 7px 12px;
    font-size: 12.5px;
    color: var(--theme-text-primary);
  }

  &__chip-row {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  &__sev-chip {
    font-size: 10.5px;
    font-weight: 700;
    padding: 4px 9px;
    border-radius: 12px;
    border: 1px solid var(--theme-border);
    background: var(--theme-bg-card);
    color: var(--theme-text-secondary);
    cursor: pointer;
    text-transform: capitalize;

    &--worse {
      text-transform: none;
    }

    &--active-all { background: var(--theme-text-primary); border-color: var(--theme-text-primary); color: #fff; }
    &--active-worse { background: #8a5a0d; border-color: #8a5a0d; color: #fff; }
    &--active-info { background: #e4f1fa; border-color: #b7d9ec; color: #1f5c80; }
    &--active-warning { background: #fbeeda; border-color: #f0c785; color: #8a5a0d; }
    &--active-error { background: #f8dede; border-color: #eeb8b8; color: #a53232; }
    &--active-critical { background: #f8dede; border-color: #a53232; color: #a53232; }
  }

  &__type-chip {
    font-size: 10.5px;
    font-weight: 600;
    padding: 4px 9px;
    border-radius: 12px;
    border: 1px solid var(--theme-border);
    background: var(--theme-bg-card);
    color: var(--theme-text-secondary);
    cursor: pointer;

    &--active {
      background: var(--theme-accent-light);
      border-color: var(--theme-accent-dark);
      color: var(--theme-accent-dark);
      font-weight: 700;
    }
  }

  &__active-filters {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    padding: 8px 16px;
    border-bottom: 1px solid var(--theme-border-soft);
    background: var(--theme-bg-content);
  }

  &__active-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 10.5px;
    font-weight: 600;
    padding: 3px 6px 3px 9px;
    border-radius: 11px;
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    color: var(--theme-text-secondary);
  }

  &__active-chip-x {
    border: none;
    background: none;
    color: var(--theme-text-tertiary);
    font-size: 13px;
    line-height: 1;
    cursor: pointer;
    padding: 0 2px;

    &:hover {
      color: var(--theme-text-primary);
    }
  }

  &__clear-all {
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .03em;
    color: var(--theme-accent-dark, #1f5c80);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0 4px;
  }

  &__follow {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 12px;
    color: var(--theme-text-secondary);
    font-weight: 600;
    cursor: pointer;
  }

  &__pulse {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--theme-status-success);
  }

  &__icon-btn {
    font-size: 11px;
    font-weight: 600;
    border: 1px solid var(--theme-border);
    background: var(--theme-bg-card);
    border-radius: var(--theme-radius-sm, 3px);
    padding: 6px 10px;
    cursor: pointer;
  }

  &__tail {
    background: var(--theme-bg-sidebar);
    padding: 14px 18px;
    font-family: var(--theme-font-mono);
    font-size: 12px;
    line-height: 2;
    color: var(--theme-text-sidebar, #b9cbca);
    max-height: 420px;
    overflow-y: auto;
  }

  &__line {
    white-space: pre-wrap;
    word-break: break-word;
  }

  &__gap {
    white-space: pre-wrap;
    word-break: break-word;
    font-style: italic;
    color: #6f8988;
    border-top: 1px dashed #3a4f4e;
    border-bottom: 1px dashed #3a4f4e;
    padding: 2px 0;
  }

  &__time {
    color: #5b7473;
    margin-right: 8px;
  }

  &__lvl {
    margin-right: 6px;
    font-weight: 600;

    &.lvl-info { color: #7fb0d6; }
    &.lvl-warning { color: #e0a030; }
    &.lvl-error, &.lvl-critical { color: #e08080; font-weight: 700; }
  }

  &__src {
    display: inline-block;
    margin-right: 8px;
    padding: 0 5px;
    border-radius: 3px;
    background: rgba(185, 203, 202, 0.14);
    color: #9fb8b7;
    font-size: 10.5px;
    font-weight: 600;
    white-space: nowrap;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: baseline;
  }

  &__expand-btn {
    display: inline;
    margin-left: 6px;
    border: none;
    background: none;
    padding: 0;
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .03em;
    color: #7fb0d6;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  &__show-more {
    text-align: center;
    padding: 10px 0 2px;
  }

  &__show-more-btn {
    font-size: 11px;
    font-weight: 700;
    border: 1px solid var(--theme-border-strong, #3a4f4e);
    background: none;
    color: #9fb8b7;
    border-radius: var(--theme-radius-sm, 3px);
    padding: 5px 12px;
    cursor: pointer;

    &:hover {
      color: #fff;
    }
  }

  &__no-entries {
    color: #6f8988;
    text-align: center;
    padding: 20px 24px;
    line-height: 1.6;
  }

  &__subscribe-cta {
    margin-top: 10px;
    font-size: 11.5px;
    font-weight: 700;
    padding: 7px 14px;
    border-radius: var(--theme-radius, 6px);
    border: 1px solid var(--theme-status-success, #2e9e6b);
    background: var(--theme-status-success, #2e9e6b);
    color: #fff;
    cursor: pointer;
  }

  &__footer {
    padding: 9px 16px;
    border-top: 1px solid var(--theme-border-dark);
    background: var(--theme-bg-sidebar);
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 11px;
    color: #6f8988;
  }
}
</style>
